import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable } from 'rxjs';
import { ContractTerminationReasonService } from 'src/app/shared/store/contract-termination-reason/contract-termination-reason.service';
import { ContractTerminationReasonModel } from 'src/app/shared/store/contract-termination-reason/types';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { View } from '../../../../shared/store/view/types';
import { ViewService } from '../../../../shared/store/view/views.service';
import { take } from 'rxjs/operators';
import { FormService } from '../../../../shared/store/form/form.service';

@UntilDestroy()
@Component({
  selector: 'app-contract-termination-reason-edit',
  templateUrl: './contract-termination-reason-edit.component.html',
  styleUrls: ['./contract-termination-reason-edit.component.scss'],
  standalone: false,
})
export class ContractTerminationReasonEditComponent
  implements OnInit, OnDestroy
{
  contractTerminationReason$: Observable<ContractTerminationReasonModel>;
  contractTerminationReasonForm: FormGroup;
  view$: Observable<View>;

  constructor(
    private readonly contractTerminationReasonService: ContractTerminationReasonService,
    private readonly subHeaderService: SubHeaderService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.contractTerminationReason$ =
      this.contractTerminationReasonService.getById.value$;
    this.view$ = this.viewService.getViews.value$;
  }

  ngOnInit(): void {
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'contractTerminationReasonUpdate') this.onSubmit();
        if (x == 'contractTerminationReasonDetails') {
          this.formService.clear();
          this.router.navigate([
            'Insurance/ContractTerminationReasons/Details/',
            this.route.snapshot.params['id'],
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.contractTerminationReasonService.getById.call(
      this.route.snapshot.params['id'],
    );

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.contractTerminationReasonForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      active: [false],
    });

    this.contractTerminationReason$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@contractTerminationReasonEdit', x.name);
        this.contractTerminationReasonForm.patchValue({
          name: x.name,
          active: x.active,
        });
      });
  }

  onSubmit(): void {
    if (this.contractTerminationReasonForm.invalid) {
      this.contractTerminationReasonForm.markAllAsTouched();
      this.formService.countErrors(this.contractTerminationReasonForm, true);
      return;
    }

    this.formService.clear();

    let result$ = this.contractTerminationReasonService.update.call({
      id: this.route.snapshot.params['id'],
      name: this.contractTerminationReasonForm.value.name!,
      active: this.contractTerminationReasonForm.value.active!,
    });
    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.router.navigate([
          '/Insurance/ContractTerminationReasons/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    this.contractTerminationReasonService.update.reset();
    this.breadcrumbService.set('@contractTerminationReasonEdit', ' ');
  }
}
